import { useReducer, useEffect } from 'react';
import { useQuizProgress } from './useLocalStorage';
import doshaQuestions from '../data/doshaQuestions';

// Quiz state reducer
const quizReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    
    case 'SET_ANSWER':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.category]: {
            ...state.answers[action.payload.category],
            [action.payload.questionId]: action.payload.answer
          }
        }
      };
    
    case 'NEXT_STEP':
      return { 
        ...state, 
        currentStep: Math.min(state.currentStep + 1, state.totalSteps - 1) 
      };
    
    case 'PREVIOUS_STEP':
      return { 
        ...state, 
        currentStep: Math.max(state.currentStep - 1, 0) 
      };
    
    case 'RESET_QUIZ':
      return {
        ...state,
        currentStep: 0,
        answers: {},
        isComplete: false
      };
    
    case 'COMPLETE_QUIZ':
      return { ...state, isComplete: true };
    
    case 'LOAD_PROGRESS':
      return {
        ...state,
        currentStep: action.payload.currentStep,
        answers: action.payload.answers
      };
    
    default:
      return state;
  }
};

// Custom hook for managing quiz state
export const useQuizState = () => {
  const { progress, saveProgress, clearProgress, hasProgress } = useQuizProgress();
  
  // Get quiz categories and calculate total steps
  const categories = Object.keys(doshaQuestions);
  const totalSteps = categories.length;
  
  const initialState = {
    currentStep: 0,
    answers: {},
    totalSteps,
    categories,
    isComplete: false
  };

  const [state, dispatch] = useReducer(quizReducer, initialState);

  // Load progress on mount if available
  useEffect(() => {
    if (hasProgress()) {
      dispatch({
        type: 'LOAD_PROGRESS',
        payload: {
          currentStep: progress.currentStep,
          answers: progress.answers
        }
      });
    }
  }, []); // Only run on mount

  // Actions
  const setCurrentStep = (step) => {
    dispatch({ type: 'SET_CURRENT_STEP', payload: step });
  };

  const setAnswer = (category, questionId, answer) => {
    dispatch({
      type: 'SET_ANSWER',
      payload: { category, questionId, answer }
    });
    
    // Save progress immediately after setting answer
    const updatedAnswers = {
      ...state.answers,
      [category]: {
        ...state.answers[category],
        [questionId]: answer
      }
    };
    saveProgress(state.currentStep, updatedAnswers);
  };

  const nextStep = () => {
    const newStep = Math.min(state.currentStep + 1, state.totalSteps - 1);
    dispatch({ type: 'NEXT_STEP' });
    saveProgress(newStep, state.answers);
  };

  const previousStep = () => {
    const newStep = Math.max(state.currentStep - 1, 0);
    dispatch({ type: 'PREVIOUS_STEP' });
    saveProgress(newStep, state.answers);
  };

  const resetQuiz = () => {
    dispatch({ type: 'RESET_QUIZ' });
    clearProgress();
  };

  const completeQuiz = () => {
    dispatch({ type: 'COMPLETE_QUIZ' });
  };

  // Helper functions
  const getCurrentCategory = () => {
    return categories[state.currentStep] || null;
  };

  const getCurrentCategoryData = () => {
    const category = getCurrentCategory();
    return category ? doshaQuestions[category] : null;
  };

  const getAnswersForCategory = (category) => {
    return state.answers[category] || {};
  };

  const isStepComplete = (stepIndex) => {
    const category = categories[stepIndex];
    if (!category) return false;
    
    const categoryAnswers = state.answers[category] || {};
    const categoryQuestions = doshaQuestions[category].questions;
    
    return categoryQuestions.every(question => 
      categoryAnswers[question.id] !== undefined
    );
  };

  const getCompletedSteps = () => {
    return categories.filter((_, index) => isStepComplete(index)).length;
  };

  const getProgressPercentage = () => {
    return Math.round((getCompletedSteps() / totalSteps) * 100);
  };

  const canProceedToNext = () => {
    return isStepComplete(state.currentStep);
  };

  const canGoToPrevious = () => {
    return state.currentStep > 0;
  };

  const isLastStep = () => {
    return state.currentStep === totalSteps - 1;
  };

  const isFirstStep = () => {
    return state.currentStep === 0;
  };

  const isQuizComplete = () => {
    return categories.every((_, index) => isStepComplete(index));
  };

  // Get all answers in nested structure for scoring
  const getAllAnswers = () => {
    return state.answers;
  };

  // Validation helpers
  const validateCurrentStep = () => {
    const categoryData = getCurrentCategoryData();
    if (!categoryData) return { isValid: false, errors: ['Invalid step'] };

    const categoryAnswers = getAnswersForCategory(getCurrentCategory());
    const missingAnswers = categoryData.questions.filter(
      question => !categoryAnswers[question.id]
    );

    return {
      isValid: missingAnswers.length === 0,
      errors: missingAnswers.map(q => `Please answer: ${q.question}`),
      missingQuestions: missingAnswers
    };
  };

  return {
    // State
    currentStep: state.currentStep,
    answers: state.answers,
    totalSteps: state.totalSteps,
    categories: state.categories,
    isComplete: state.isComplete,
    
    // Actions
    setCurrentStep,
    setAnswer,
    nextStep,
    previousStep,
    resetQuiz,
    completeQuiz,
    
    // Helpers
    getCurrentCategory,
    getCurrentCategoryData,
    getAnswersForCategory,
    isStepComplete,
    getCompletedSteps,
    getProgressPercentage,
    canProceedToNext,
    canGoToPrevious,
    isLastStep,
    isFirstStep,
    isQuizComplete,
    getAllAnswers,
    validateCurrentStep,
    
    // Progress
    hasProgress: hasProgress(),
    clearProgress
  };
};

export default useQuizState;
